import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, ChefHat, Package, Download, Home, Store, Loader2, XCircle } from "lucide-react";
import { rupee2 } from "../lib/format";
import { api, API } from "../lib/api";

const STEPS = ["NEW", "ACCEPTED", "PREPARING", "READY", "COMPLETED"];
const LABELS = { NEW: "Order Placed", ACCEPTED: "Accepted", PREPARING: "Preparing", READY: "Ready to Collect", COMPLETED: "Completed", REJECTED: "Cancelled" };
const COLORS = {
  NEW: "text-blue-400 bg-blue-500/15", ACCEPTED: "text-cyan-400 bg-cyan-500/15",
  PREPARING: "text-amber-400 bg-amber-500/15", READY: "text-emerald-400 bg-emerald-500/15",
  COMPLETED: "text-emerald-400 bg-emerald-500/15", REJECTED: "text-red-400 bg-red-500/15",
};

export default function Track() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  const fetchOrder = () => api.get(`/public/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
  useEffect(() => {
    fetchOrder();
    const t = setInterval(fetchOrder, 5000);
    return () => clearInterval(t);
  }, [id]);

  if (!order) return <div className="max-w-md mx-auto min-h-screen bg-[#08183A] flex items-center justify-center"><Loader2 className="animate-spin text-amber-400" /></div>;

  const masterDone = order.status === "COMPLETED";

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#08183A] pb-10">
      <div className="gold-gradient text-slate-950 px-5 pt-8 pb-6 rounded-b-3xl">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={26} />
          <span className="font-display font-extrabold text-xl">Order Confirmed</span>
        </div>
        <div className="text-sm mt-1 font-mono">{order.order_number}</div>
        <div className="text-xs opacity-80 mt-1">{order.mall_name} · {order.foodcourt_name} · Table {order.table_code}</div>
        <div className="mt-4 flex items-center justify-between bg-slate-950/15 rounded-xl px-4 py-2.5">
          <span className="text-sm font-semibold">Total Paid</span>
          <span className="font-display font-extrabold text-lg">{rupee2(order.payable)}</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="text-center">
          <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${COLORS[order.status] || "text-slate-300 bg-slate-700"}`} data-testid="master-status">
            {order.status === "PARTIALLY_FULFILLED" ? "PARTIALLY FULFILLED" : order.status.replace("_", " ")}
          </span>
        </div>

        {order.suborders.map((s) => (
          <SubOrderCard key={s.id} s={s} />
        ))}

        <div className="flex gap-3">
          <a href={`${API}/public/orders/${order.id}/bill.pdf`} target="_blank" rel="noreferrer"
             className="flex-1 card-surface rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-semibold hover:border-amber-500 transition-colors" data-testid="download-bill-btn">
            <Download size={16} className="text-amber-400" /> Download Bill
          </a>
          <button onClick={() => navigate("/order")} className="flex-1 card-surface rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-semibold hover:border-amber-500 transition-colors" data-testid="order-more-btn">
            <Store size={16} className="text-amber-400" /> Order More
          </button>
        </div>

        {masterDone && (
          <button onClick={() => navigate("/")} className="w-full gold-btn rounded-xl py-3 flex items-center justify-center gap-2 text-sm" data-testid="track-home-btn">
            <Home size={16} /> Done
          </button>
        )}
      </div>
    </div>
  );
}

function SubOrderCard({ s }) {
  const rejected = s.status === "REJECTED";
  const currentIdx = STEPS.indexOf(s.status);
  return (
    <div className="card-surface rounded-2xl p-4" data-testid={`suborder-${s.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Store size={15} className="text-amber-400" />
          <span className="font-semibold text-sm">{s.restaurant_name}</span>
        </div>
        <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${COLORS[s.status]}`}>{LABELS[s.status] || s.status}</span>
      </div>

      {!rejected ? (
        <div className="flex items-center justify-between px-1">
          {[
            { key: "ACCEPTED", icon: CheckCircle2 },
            { key: "PREPARING", icon: ChefHat },
            { key: "READY", icon: Package },
            { key: "COMPLETED", icon: CheckCircle2 },
          ].map((step, i, arr) => {
            const done = currentIdx >= STEPS.indexOf(step.key);
            return (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center gap-1">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center ${done ? "gold-gradient text-slate-950" : "card-surface-2 text-slate-600"}`}>
                    <step.icon size={15} />
                  </div>
                  <span className={`text-[9px] ${done ? "text-amber-400" : "text-slate-600"}`}>{LABELS[step.key]}</span>
                </div>
                {i < arr.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${currentIdx > STEPS.indexOf(step.key) ? "bg-amber-500" : "bg-slate-700"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-red-400 text-xs"><XCircle size={15} /> Cancelled · refund initiated</div>
      )}

      <div className="mt-3 pt-3 border-t hairline text-xs text-slate-400 space-y-1">
        {s.items.map((it, i) => (
          <div key={i} className="flex justify-between"><span>{it.name} × {it.qty}</span><span>{rupee2(it.line_total)}</span></div>
        ))}
        <div className="flex justify-between text-slate-200 font-semibold pt-1"><span>Subtotal</span><span>{rupee2(s.total)}</span></div>
      </div>
    </div>
  );
}
